/**
 * User support tickets.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';
import { ticketsAPI } from '../api/tickets';
import { EmptyState } from '../components/ui/EmptyState';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { THEMED_SURFACE_COLORS as C, ThemedChip, ThemedField, ThemedInput, ThemedNotice, ThemedPanel, ThemedSelect, ThemedSubmitButton, ThemedTextarea, ThemedBottomActionBar } from '../components/ui/ThemedSurface';

type TicketStatus = 'open' | 'pending_user' | 'pending_admin' | 'resolved' | 'closed';
type TicketView = 'list' | 'create' | 'detail';

const CATEGORY_OPTIONS = [
  'feedback',
  'account',
  'recharge',
  'withdraw',
  'ep',
  'otc',
  'kyc',
  'bug',
  'other',
];

interface TicketRow {
  id: number;
  category: string;
  title: string;
  body: string;
  status: TicketStatus;
  priority: string;
  create_time: string;
  update_time: string;
}

interface TicketReply {
  id: number;
  sender_type: 'user' | 'admin';
  sender_id: number;
  body: string;
  create_time: string;
}

interface TicketDetail {
  ticket: TicketRow;
  replies: TicketReply[];
}

function statusText(t: ReturnType<typeof useTranslation>['t'], status: TicketStatus) {
  return t(`tickets.status.${status}`);
}

function formatTicketTime(value: string) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.replace('T', ' ').slice(0, 16);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}



function TicketCard({ ticket, onOpen }: { ticket: TicketRow; onOpen: (id: number) => void }) {
  const { t } = useTranslation();
  const active = ticket.status !== 'closed';

  return (
    <button type="button" onClick={() => onOpen(ticket.id)} className="block w-full text-left">
      <ThemedPanel className="p-0 transition active:scale-[0.99]">
        <div className="relative px-4 py-4">
          <div className="pointer-events-none absolute inset-x-4 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent 0%, ${active ? C.gold : C.softBorder} 50%, transparent 100%)` }} />
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="mb-2 flex items-center gap-2">
                <span className="rounded-full border px-2 py-0.5 text-[10px] font-semibold" style={{ borderColor: C.softBorder, background: C.inputBg, color: C.gold }}>
                  #{ticket.id}
                </span>
                <span className="truncate text-[10px]" style={{ color: C.muted }}>
                  {t(`tickets.categories.${ticket.category}`, ticket.category)}
                </span>
              </div>
              <div className="truncate text-base font-semibold leading-tight" style={{ color: C.text }}>{ticket.title}</div>
            </div>
            <ThemedChip active={active}>{statusText(t, ticket.status)}</ThemedChip>
          </div>
          <div className="mt-1 line-clamp-2 text-sm leading-relaxed" style={{ color: C.muted }}>{ticket.body}</div>
          <div className="mt-3 flex items-center justify-between gap-3 border-t pt-3" style={{ borderColor: C.line }}>
            <span className="truncate text-[10px]" style={{ color: C.muted }}>{formatTicketTime(ticket.create_time)}</span>
            <span className="text-[10px] font-semibold" style={{ color: C.gold }}>{t('tickets.ticketDetail')}</span>
          </div>
        </div>
      </ThemedPanel>
    </button>
  );
}

function TicketDetailCard({ ticket }: { ticket: TicketRow }) {
  const { t } = useTranslation();
  const active = ticket.status !== 'closed';

  return (
    <ThemedPanel className="p-0">
      <div className="relative px-4 py-4">
        <div className="pointer-events-none absolute inset-x-4 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent 0%, ${active ? C.gold : C.softBorder} 50%, transparent 100%)` }} />
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex items-center gap-2">
              <span className="rounded-full border px-2 py-0.5 text-[10px] font-semibold" style={{ borderColor: C.softBorder, background: C.inputBg, color: C.gold }}>
                #{ticket.id}
              </span>
              <span className="truncate text-[10px]" style={{ color: C.muted }}>
                {t(`tickets.categories.${ticket.category}`, ticket.category)}
              </span>
            </div>
            <div className="text-lg font-semibold leading-tight" style={{ color: C.text }}>{ticket.title}</div>
          </div>
          <ThemedChip active={active}>{statusText(t, ticket.status)}</ThemedChip>
        </div>
        <div className="mt-1 whitespace-pre-wrap text-sm leading-relaxed" style={{ color: C.text }}>{ticket.body}</div>
        <div className="mt-3 flex items-center justify-between gap-3 border-t pt-3" style={{ borderColor: C.line }}>
          <span className="truncate text-[10px]" style={{ color: C.muted }}>{formatTicketTime(ticket.create_time)}</span>
          <span className="truncate text-[10px]" style={{ color: C.muted }}>{formatTicketTime(ticket.update_time)}</span>
        </div>
      </div>
    </ThemedPanel>
  );
}

function ConversationPanel({ replies }: { replies: TicketReply[] }) {
  const { t } = useTranslation();

  return (
    <ThemedPanel className="p-0">
      <div className="relative px-4 py-4">
        <div className="pointer-events-none absolute inset-x-4 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent 0%, ${C.gold}66 50%, transparent 100%)` }} />
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="text-sm font-semibold leading-none" style={{ color: C.text }}>{t('tickets.conversationHistory')}</div>
          <span className="rounded-full border px-3 py-1 text-[10px] font-semibold leading-none" style={{ borderColor: C.softBorder, background: C.inputBg, color: C.gold }}>
            {replies.length}
          </span>
        </div>
        {replies.length === 0 ? (
          <div className="rounded-xl border px-3 py-3 text-sm" style={{ borderColor: C.line, background: C.inputBg, color: C.muted }}>{t('tickets.noReplies')}</div>
        ) : (
          <ul className="flex flex-col gap-3">
            {replies.map((item) => {
              const admin = item.sender_type === 'admin';

              return (
                <li key={item.id} className={`flex items-end gap-2 ${admin ? 'justify-start' : 'justify-end'}`}>
                  {admin && (
                    <div className="flex size-8 shrink-0 items-center justify-center rounded-full border text-[10px] font-bold" style={{ borderColor: `${C.gold}66`, background: C.chipBg, color: C.gold }}>
                      CS
                    </div>
                  )}
                  <div className={`max-w-[78%] ${admin ? 'text-left' : 'text-right'}`}>
                    <div className={`mb-1 flex items-center gap-2 text-[10px] ${admin ? 'justify-start' : 'justify-end'}`} style={{ color: C.muted }}>
                      <span className="font-semibold" style={{ color: admin ? C.gold : C.text }}>{admin ? t('tickets.support') : t('tickets.me')}</span>
                      <span>{formatTicketTime(item.create_time)}</span>
                    </div>
                    <div
                      className={`rounded-2xl border px-3 py-2.5 text-left shadow-[0_8px_20px_rgba(0,0,0,0.18)] ${admin ? 'rounded-bl-md' : 'rounded-br-md'}`}
                      style={{ borderColor: admin ? `${C.gold}55` : `${C.softBorder}`, background: admin ? C.chipBg : 'rgba(0,0,0,0.30)' }}
                    >
                      <div className="whitespace-pre-wrap text-sm leading-relaxed" style={{ color: C.text }}>{item.body}</div>
                    </div>
                  </div>
                  {!admin && (
                    <div className="flex size-8 shrink-0 items-center justify-center rounded-full border text-[10px] font-bold" style={{ borderColor: C.softBorder, background: C.inputBg, color: C.text }}>
                      ME
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </ThemedPanel>
  );
}

export function Tickets() {
  const { t } = useTranslation();
  const [view, setView] = useState<TicketView>('list');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const title = view === 'create' ? t('tickets.createTicket') : view === 'detail' ? t('tickets.ticketDetail') : t('tickets.myTickets');

  const goList = () => {
    setView('list');
    setSelectedId(null);
  };

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader
          title={title}
          onBack={view === 'list' ? () => window.history.back() : goList}
          theme={C}
          action={view === 'list' ? t('tickets.createNew') : undefined}
          onAction={view === 'list' ? () => setView('create') : undefined}
        />
        <div className="flex flex-col gap-4 overflow-y-auto px-5 pt-[70px]" style={{ height: 'calc(100% - 70px)', paddingBottom: view === 'create' || view === 'detail' ? '76px' : '12px' }}>
          {view === 'list' && <TicketList onOpen={(id) => { setSelectedId(id); setView('detail'); }} />}
          {view === 'create' && <CreateTicket onSubmitted={goList} />}
          {view === 'detail' && selectedId !== null && <TicketDetailView id={selectedId} />}
        </div>
      </div>
    </div>
  );
}

function TicketList({ onOpen }: { onOpen: (id: number) => void }) {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ['tickets', 'mine'],
    queryFn: async () =>
      (await ticketsAPI.getTickets({ page: 1, limit: 50 })) as unknown as { total: number; rows: TicketRow[] },
  });

  if (isLoading) return <LoadingSkeleton variant="list" lines={5} />;

  const rows = data?.rows ?? [];
  if (rows.length === 0) {
    return <EmptyState title={t('tickets.noTickets')} description={t('tickets.noTicketsHint')} />;
  }

  return (
    <div className="flex flex-col gap-3">
      {rows.map((ticket) => <TicketCard key={ticket.id} ticket={ticket} onOpen={onOpen} />)}
    </div>
  );
}

function CreateTicket({ onSubmitted }: { onSubmitted: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [category, setCategory] = useState('feedback');
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const categoryOptions = CATEGORY_OPTIONS.map((option) => ({ value: option, label: t(`tickets.categories.${option}`) }));

  const submit = useMutation({
    mutationFn: () =>
      ticketsAPI.create({ category, title: title.trim(), body: body.trim() } as unknown as Parameters<
        typeof ticketsAPI.create
      >[0]),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tickets', 'mine'] });
      setCategoryDropdownOpen(false);
      setTitle('');
      setBody('');
      setError(null);
      onSubmitted();
    },
    onError: (e: unknown) => setError(e instanceof ApiError ? e.message : t('tickets.submitFailed')),
  });

  function onSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    if (!title.trim() || !body.trim()) {
      setError(t('tickets.titleBodyRequired'));
      return;
    }
    submit.mutate();
  }

  return (
    <>
      <ThemedPanel className="p-4">
        <form id="create-ticket-form" onSubmit={onSubmit} className="flex flex-col gap-3">
        <ThemedField label={t('tickets.categoryLabel')} required>
          <ThemedSelect
            value={category}
            options={categoryOptions}
            open={categoryDropdownOpen}
            onToggle={() => setCategoryDropdownOpen((open) => !open)}
            onSelect={(value) => {
              setCategory(value);
              setCategoryDropdownOpen(false);
            }}
          />
        </ThemedField>
        <ThemedField label={t('tickets.title')} required>
          <ThemedInput type="text" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder={t('tickets.titlePlaceholder')} />
        </ThemedField>
        <ThemedField label={t('tickets.description')} required>
          <ThemedTextarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} maxLength={10000} placeholder={t('tickets.descriptionPlaceholder')} />
        </ThemedField>
        {error && <ThemedNotice tone="error">{error}</ThemedNotice>}
      </form>
      </ThemedPanel>
      <ThemedBottomActionBar>
        <ThemedSubmitButton type="submit" disabled={submit.isPending} form="create-ticket-form">{submit.isPending ? '…' : t('tickets.submitTicket')}</ThemedSubmitButton>
      </ThemedBottomActionBar>
    </>
  );
}

function TicketDetailView({ id }: { id: number }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [reply, setReply] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['tickets', 'detail', id],
    queryFn: async () => (await ticketsAPI.getById(Number(id))) as unknown as TicketDetail,
  });

  const sendReply = useMutation({
    mutationFn: () =>
      ticketsAPI.reply(Number(id), { body: reply.trim() } as unknown as Parameters<typeof ticketsAPI.reply>[1]),
    onSuccess: () => {
      setReply('');
      setError(null);
      qc.invalidateQueries({ queryKey: ['tickets', 'detail', id] });
      qc.invalidateQueries({ queryKey: ['tickets', 'mine'] });
    },
    onError: (e: unknown) => setError(e instanceof ApiError ? e.message : t('tickets.sendFailed')),
  });

  function onSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    if (!reply.trim()) {
      setError(t('tickets.replyRequired'));
      return;
    }
    sendReply.mutate();
  }

  if (isLoading || !data) return <LoadingSkeleton variant="card" />;

  const { ticket, replies } = data;
  const canReply = ticket.status !== 'closed' && ticket.status !== 'resolved';

  return (
    <div className="flex flex-col gap-3">
      <TicketDetailCard ticket={ticket} />

      <ConversationPanel replies={replies} />

      {canReply && (
        <>
          <ThemedPanel className="p-4">
            <form id="ticket-reply-form" onSubmit={onSubmit} className="flex flex-col gap-3">
            <ThemedTextarea value={reply} onChange={(e) => setReply(e.target.value)} rows={4} maxLength={10000} placeholder={t('tickets.enterReply')} />
            {error && <ThemedNotice tone="error">{error}</ThemedNotice>}
          </form>
          </ThemedPanel>
          <ThemedBottomActionBar>
            <ThemedSubmitButton type="submit" disabled={sendReply.isPending} form="ticket-reply-form">{sendReply.isPending ? '…' : t('tickets.sendReply')}</ThemedSubmitButton>
          </ThemedBottomActionBar>
        </>
      )}
    </div>
  );
}
